import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {RemoveRelatesToMutationMutation} from '@github-ui/server-persisted-queries/types/removeRelatesToMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {RELATES_TO_CONNECTION_PATH} from '../components/sections/relations-section/relates-to-query-types'

type RemoveRelatesToVariables = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
}

type RemoveRelatesToOperation = Extract<ConnectionOperation, {type: 'removeFromConnection'}>

function toOptimisticOperation(relatedIssueId: string): RemoveRelatesToOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: RELATES_TO_CONNECTION_PATH,
    nodeId: relatedIssueId,
    counts: [{path: [...RELATES_TO_CONNECTION_PATH, 'totalCount'], delta: -1}],
  }
}

async function removeRelatesToRequest({
  nameWithOwner,
  issueNumber,
  relatedIssueId,
}: RemoveRelatesToVariables & {relatedIssueId: string}): Promise<RemoveRelatesToMutationMutation> {
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relates_to.json`, {
    method: 'DELETE',
    body: {relatedIssueId},
  })
  const result = (await response.json()) as {
    data?: RemoveRelatesToMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message ?? result.data?.removeRelatesTo?.errors[0]?.message
  if (!response.ok || error || !result.data?.removeRelatesTo?.issue) {
    throw new Error(error ?? 'Failed to remove related issue')
  }
  return result.data
}

export function useRemoveRelatesToMutation({issueId, nameWithOwner, issueNumber}: RemoveRelatesToVariables) {
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const {mutateAsync} = useConnectionMutation<RemoveRelatesToMutationMutation>({
    ownerId: issueId,
    connectionPath: RELATES_TO_CONNECTION_PATH,
    mutationFn: operation =>
      removeRelatesToRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        relatedIssueId: (operation as RemoveRelatesToOperation).nodeId,
      }),
    onSuccess: async data => {
      const issue = data.removeRelatesTo?.issue
      if (issue) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey: secondaryQueryKey}],
          ownerId: issueId,
          operation: {type: 'setFields', id: issue.id, fields: {topRelatesTo: issue.topRelatesTo}},
        })
      }
      await queryClient.invalidateQueries({queryKey: pickerQueryKey})
    },
  })

  return useCallback(
    (relatedIssueId: string) => mutateAsync({operation: toOptimisticOperation(relatedIssueId)}),
    [mutateAsync],
  )
}
